import { PickerPlan, PickerPremium, PickerTopup } from '../../../lib/plans';
import { activePremiumSubscription } from './settings-plan';

/** The catalog option the account's active subscription maps to — the one derivation of "which option
 *  is this account on", read below for both the Max+ decision and the top-up pack it carries. */
function activeCatalogOption(premium: PickerPremium | null, plans: PickerPlan[] | null) {
  const sub = activePremiumSubscription(premium);
  return (plans ?? [])
    .flatMap((p) => p.options.map((option) => ({ key: p.key, option })))
    .find((x) => x.option.agentPlanId === sub?.planId);
}

/**
 * The one-time top-up pack the account's current plan option offers, or null. Max+ only today — the
 * catalog hangs the pack off that option — and null is a real answer there too: a Max+ subscriber on a
 * sibling option carries no pack.
 *
 * The ONE web definition, because three surfaces now gate on it: both account cards' "Get more
 * credits" primary (via `useSettingsPlanView`) and the drawer credits card's top-up CTA (via
 * `useCreditsSummary`). It used to be derived inline in `useSettingsPlanView` alongside a second copy
 * of the option lookup below; a third copy in the drawer is how the drawer would come to offer a
 * top-up the account page doesn't.
 *
 * Pure, like `activePlanIsMaxPlus` — keep it that way.
 */
export function activePlanTopup(premium: PickerPremium | null, plans: PickerPlan[] | null): PickerTopup | null {
  return activeCatalogOption(premium, plans)?.option.topup ?? null;
}

// Is the account's active plan the top ("max"/Max+) catalog tier? This is the ONE web
// definition of that decision — the plan card's CTA reads "Change" instead of "Upgrade"
// on it (Monday 12757143948), and it gates the top-up button. Shared by the account cards
// (via useSettingsPlanView) AND the always-mounted drawer credits card (useCreditsSummary),
// so the two surfaces can never disagree about which plan is Max+.
//
// The native apps make the same decision by matching the credits-overview plan id against a
// fixed Max+ GUID (`SettingsPlan.isMaxPlusPlan`); web keys on the catalog `key === 'max'`
// instead because that is the signal the settings surfaces already carry (and the same
// catalog option drives the top-up pack). Both identify the same plan.
//
// Pure so it is unit-tested (`test/max-plus.test.ts`) without the app graph — keep it that
// way; do not add a runtime import here.
export function activePlanIsMaxPlus(premium: PickerPremium | null, plans: PickerPlan[] | null): boolean {
  return activeCatalogOption(premium, plans)?.key === 'max';
}
